import { toast } from 'sonner';
import { PlayIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useMoveTask } from '../hooks/useMoveTask';
import { useOpenLinkedAccounts } from '@/features/identity';
import { refusalToastOptions } from '../moveRefusalToast';
import { isBusy } from '../executionStatus';
import { MOVE_FAILED, clientBusyRefusal } from '../errors';
import type { BoardStepSection } from '../boardModel';
import type { TaskView } from '@/lib/api/generated/model';
import { transitionCondition } from '../transitionCondition';
import { ACCESS_COPY } from '@/features/workspaces/accessCopy';
import { useCanWork } from '@/features/workspaces/canWork';

export interface TaskTransitionsProps {
  workspaceId: string;
  task: TaskView;
  /** The step the task is standing on. Its transitions are the paths on offer. */
  step: BoardStepSection;
  /** Names the target steps, so a transition can be labelled by where it goes. */
  stepLabels: Map<string, string>;
}

/**
 * The paths declared out of the step this task is standing on, each offered as
 * a control that starts it.
 *
 * Worded as running a step, not as filing the task somewhere, because that is
 * what happens: `move_task` places the task *and* starts an execution of the
 * target step in one transaction. A control reading "Implement" beside a
 * control reading "Review" looks like a filing cabinet; "Run Implement" says
 * what it costs.
 *
 * No confirmation. Dragging the card to the same column on the board fires the
 * identical mutation with none, and a dialog on one path but not the other
 * would teach a person that the two do different things. What guards against
 * the accident instead is the same guard the drag has: a busy task cannot be
 * started, and the check runs here before any request is made.
 *
 * Only declared transitions. The service enforces no legality at all — any real
 * step of the workflow is an acceptable target (BR-002-28) — so this list is a
 * narrowing this component chooses, not one the server imposes. Moving a task
 * somewhere no transition reaches is what dragging is for.
 */
function TaskTransitionsControl({
  workspaceId,
  task,
  step,
  stepLabels,
}: TaskTransitionsProps) {
  const move = useMoveTask();
  const openLinkedAccounts = useOpenLinkedAccounts();
  const transitions = step.transitions ?? [];
  const busy = isBusy(task);

  if (transitions.length === 0) {
    /*
      The two system steps are the common case here, not the rare one: the
      contract synthesizes them outside the phase list and gives them no
      transitions at all, so every task waiting in Backlog lands on this branch.
      "This step declares no onward path" is true of Backlog and also actively
      misleading about it — Backlog is where work starts, and the reason there
      is no button is that the workflow declares no path *out* of it, not that
      the task is stuck.
    */
    return (
      <p className="text-muted-foreground text-sm">
        {step.systemRole === 'start'
          ? 'Tasks wait here until someone starts one. Drag the card to the step you want to run.'
          : step.systemRole === 'done'
            ? 'This task has finished. There is nowhere further for it to go.'
            : 'This step declares no onward path. Drag the card on the board to move this task.'}
      </p>
    );
  }

  const start = (targetStepId: string) => {
    // Refused before the request, exactly as `resolveDropRequest` refuses a
    // busy drag, and in the same words — one wording for one refusal.
    if (busy) {
      toast.error(clientBusyRefusal().message);
      return;
    }
    move.mutate(
      {
        workspaceId,
        taskId: task.id,
        targetStepId,
        expectedVersion: task.version,
      },
      {
        onSuccess: (result) => {
          if (result.refusal) {
            toast.error(
              result.refusal.message,
              refusalToastOptions(result.refusal, openLinkedAccounts),
            );
          }
        },
        onError: () => {
          toast.error(MOVE_FAILED);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {busy ? (
        <p className="text-muted-foreground text-sm">
          This task is busy right now, so no step can be started from here.
        </p>
      ) : null}

      <ul className="flex flex-col gap-2">
        {transitions.map((transition) => {
          // A transition's label is authored and may be absent. The target
          // step's name is the honest fallback — it is what the button will
          // actually run — and the raw key is the last resort, because a
          // transition can point at a step this board could not resolve.
          const target = stepLabels.get(transition.to);
          const name = transition.label ?? target ?? transition.to;

          return (
            <li key={transition.key} className="flex flex-col gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                // Full width on a phone (spec 270), a thumb's whole row.
                className="w-fit max-md:w-full"
                disabled={busy || move.isPending}
                onClick={() => {
                  start(transition.to);
                }}
              >
                <PlayIcon aria-hidden="true" strokeWidth={1.5} />
                Run {name}
              </Button>

              {/*
                The condition the workflow author wrote for this path. It is
                the only place in the product that says *why* a branch exists,
                and without it a row of buttons is a row of step names.
              */}
              {transitionCondition(transition.when, step.kind) ? (
                <p className="text-muted-foreground pl-1 text-xs">
                  {transitionCondition(transition.when, step.kind)}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Offered only to someone who may work in the workspace (spec 189): a person reading it gets nothing that would only be refused.
 */
export function TaskTransitions(props: TaskTransitionsProps) {
  return useCanWork() ? (
    <TaskTransitionsControl {...props} />
  ) : (
    <p className="text-muted-foreground text-sm">{ACCESS_COPY.membersOnly}</p>
  );
}
